import type { ApiDashboardElement } from '@highimpact-frontend/api-types';
import type { DashboardDataApi } from '../../../../api/DashboardHostApi';
import type { DashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import { setDashboardElement, setImageId } from '../../../../state/dashboard/slice';

export interface AssignDashboardElementResourceInput {
  dashboardElementId: number;
  resourceId: number;
  elementData: ApiDashboardElement;
}

export function buildElementWithResourceId(
  elementData: ApiDashboardElement,
  resourceId: number
): ApiDashboardElement | null {
  const currentResourceId = (elementData.resource_ids ?? [])[0];
  if (currentResourceId === resourceId) {
    return null;
  }

  return {
    ...elementData,
    resource_ids: [resourceId],
  };
}

export function isCompleteDashboardElement(
  elementData: ApiDashboardElement
): elementData is ApiDashboardElement {
  return typeof elementData.element_type === 'string';
}

export async function assignDashboardElementResource(
  api: DashboardDataApi,
  {
    dashboardElementId,
    resourceId,
    elementData,
  }: AssignDashboardElementResourceInput
): Promise<ApiDashboardElement | null> {
  const baseElement = isCompleteDashboardElement(elementData)
    ? elementData
    : await api.getDashboardElement(dashboardElementId);

  const updatedElement = buildElementWithResourceId(baseElement, resourceId);
  if (!updatedElement) {
    return null;
  }

  await api.putDashboardElement(dashboardElementId, updatedElement);
  return updatedElement;
}

export async function persistImageWidgetResourceAssignment(
  api: DashboardDataApi,
  dispatch: DashboardLibraryDispatch,
  {
    gridType,
    gridId,
    widgetId,
    dashboardElementId,
    resourceId,
    elementData,
  }: {
    gridType: 'reports' | 'grids';
    gridId: number;
    widgetId: string;
    dashboardElementId: number;
    resourceId: number;
    elementData: ApiDashboardElement;
  }
): Promise<void> {
  const updatedElement = await assignDashboardElementResource(api, {
    dashboardElementId,
    resourceId,
    elementData,
  });

  dispatch(
    setImageId({
      widgetId,
      dashboardId: gridId,
      resourceId,
      grid_type: gridType,
    })
  );

  if (updatedElement) {
    dispatch(
      setDashboardElement({
        gridId,
        gridType,
        widgetId,
        dashboardElement: updatedElement,
      })
    );
  }
}
